/* eslint-disable consistent-return */
import { forwardRef, useImperativeHandle, useRef } from 'react';
import {
  ConnectDragSource,
  ConnectDropTarget,
  DragSource,
  DragSourceConnector,
  DragSourceMonitor,
  DropTarget,
  DropTargetConnector,
  DropTargetMonitor,
  XYCoord,
} from 'react-dnd';
import ItemTypes from 'components/logProcessing/ScrubbingRulesList/ListItemTypes';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import EditIcon from '@mui/icons-material/Edit';
import CopyIcon from '@mui/icons-material/FileCopy';
import DeleteIcon from '@mui/icons-material/Delete';
import DescriptionIcon from '@mui/icons-material/Description';
import IconButton from 'components/common/IconButton/IconButton';

export interface ListItemProps {
  id: number;
  name: string;
  index: number;
  isWriteDisabled: boolean;
  onDrag: (dragIndex: number, hoverIndex: number) => void;
  onDrop: (id: number, position: number) => void;
  onCopy: (id: number) => void;
  onEdit: (id: number) => void;
  onDelete: (id: number, name: string) => void;

  isDragging: boolean;
  connectDragSource: ConnectDragSource;
  connectDropTarget: ConnectDropTarget;
}

interface ListItemInstance {
  getNode(): HTMLDivElement | null;
}

const ListItem = forwardRef<HTMLDivElement, ListItemProps>(
  (
    {
      id,
      name,
      isWriteDisabled,
      isDragging,
      connectDragSource,
      connectDropTarget,
      onCopy,
      onEdit,
      onDelete,
    }: ListItemProps,
    ref,
  ) => {
    const elementRef = useRef<HTMLDivElement | null>(null);
    connectDragSource(elementRef);
    connectDropTarget(elementRef);

    const opacity = isDragging ? 0 : 1;
    useImperativeHandle<{}, ListItemInstance>(ref, () => ({
      getNode: (): HTMLDivElement | null => elementRef.current,
    }));
    const buttons = isWriteDisabled ? (
      <>
        <IconButton
          className="FiltersList__ListItem__Icon"
          tooltip="View"
          onClick={(): void => onEdit(id)}
          icon={<DescriptionIcon />}
        />
      </>
    ) : (
      <>
        <IconButton
          className="FiltersList__ListItem__Icon"
          tooltip="Edit"
          onClick={(): void => onEdit(id)}
          icon={<EditIcon />}
        />
        <IconButton
          className="FiltersList__ListItem__Icon"
          tooltip="Clone"
          onClick={(): void => onCopy(id)}
          icon={<CopyIcon />}
        />
        <IconButton
          className="FiltersList__ListItem__Icon"
          tooltip="Delete"
          onClick={(): void => onDelete(id, name)}
          icon={<DeleteIcon />}
        />
      </>
    );
    return (
      <div ref={elementRef} className="FiltersList__ListItem" style={{ opacity }}>
        {isWriteDisabled ? null : <DragIndicatorIcon />}
        <span className="FiltersList__ListItem__Name">{name}</span>
        {buttons}
      </div>
    );
  },
);

export default DropTarget(
  ItemTypes.SCRUBBING_RULE,
  {
    hover(props: ListItemProps, monitor: DropTargetMonitor, component: ListItemInstance) {
      if (!component) {
        return null;
      }
      // node = HTML Div element from imperative API
      const node = component.getNode();
      if (!node) {
        return null;
      }

      const dragIndex = monitor.getItem().index;
      const hoverIndex = props.index;

      // Don't replace items with themselves
      if (dragIndex === hoverIndex) {
        return;
      }

      // Determine rectangle on screen
      const hoverBoundingRect = node.getBoundingClientRect();

      // Get vertical middle
      const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;

      // Determine mouse position
      const clientOffset = monitor.getClientOffset();

      // Get pixels to the top
      const hoverClientY = (clientOffset as XYCoord).y - hoverBoundingRect.top;

      // Only perform the move when the mouse has crossed half of the items height
      // When dragging downwards, only move when the cursor is below 50%
      // When dragging upwards, only move when the cursor is above 50%

      // Dragging downwards
      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
        return;
      }

      // Dragging upwards
      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
        return;
      }

      // Time to actually perform the action
      props.onDrag(dragIndex, hoverIndex);

      // Note: we're mutating the monitor item here!
      // Generally it's better to avoid mutations,
      // but it's good here for the sake of performance
      // to avoid expensive index searches.
      monitor.getItem().index = hoverIndex;
    },
    drop(props: ListItemProps) {
      const { id, index: position } = props;
      props.onDrop(id, position);
    },
  },
  (connect: DropTargetConnector) => ({
    connectDropTarget: connect.dropTarget(),
  }),
)(
  DragSource(
    ItemTypes.SCRUBBING_RULE,
    {
      beginDrag: (props: ListItemProps) => ({
        id: props.id,
        index: props.index,
      }),
    },
    (connect: DragSourceConnector, monitor: DragSourceMonitor) => ({
      connectDragSource: connect.dragSource(),
      isDragging: monitor.isDragging(),
    }),
  )(ListItem),
);
